import React, { useMemo, useRef, useState } from "react";
import { Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FilterOption } from "./SustainTeamTypes";

interface Props {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  /** Show a search box inside the dropdown (useful for long option lists like Manager). */
  searchable?: boolean;
  align?: "left" | "right";
}

const PANEL_WIDTH = 220;

/** Multi-select checkbox filter for a table column header, with optional in-panel search. */
const RosterColumnFilter: React.FC<Props> = ({
  label,
  options,
  selected,
  onChange,
  isOpen,
  onToggle,
  searchable,
  align = "left",
}) => {
  const [query, setQuery] = useState("");
  const triggerRef = useRef<View>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const active = selected.length > 0;

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggleValue = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const handleTriggerPress = () => {
    if (isOpen) {
      onToggle();
      return;
    }
    // Measure in window coordinates so the panel renders inside a top-level Modal,
    // unclipped by the table card's `overflow: hidden` or any scroll container bounds.
    const node = triggerRef.current as any;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, width: number, height: number) => {
        const left = align === "right" ? Math.max(8, x + width - PANEL_WIDTH) : x;
        setPanelPos({ top: y + height + 4, left });
        onToggle();
      });
    } else {
      onToggle();
    }
  };

  return (
    <View ref={triggerRef} collapsable={false} style={cf.wrap}>
      <Pressable style={cf.trigger} onPress={handleTriggerPress}>
        <Text style={cf.triggerText}>{label}</Text>
        <View style={[cf.iconWrap, active && cf.iconWrapActive]}>
          <MaterialIcons name="filter-list" size={13} color={active ? "#034895" : "#9AA4B2"} />
          {active && <View style={cf.badge}><Text style={cf.badgeText}>{selected.length}</Text></View>}
        </View>
      </Pressable>

      <Modal visible={isOpen} transparent animationType="none" onRequestClose={onToggle}>
        <View style={{ flex: 1 }}>
          <Pressable style={StyleSheet.absoluteFillObject} onPress={onToggle} />
          {panelPos && (
            <View style={[cf.panel, { top: panelPos.top, left: panelPos.left }]}>
              {searchable && (
                <View style={cf.searchWrap}>
                  <MaterialIcons name="search" size={13} color="#9a9a9a" />
                  <TextInput
                    style={cf.searchInput}
                    placeholder="Search…"
                    placeholderTextColor="#9a9a9a"
                    value={query}
                    onChangeText={setQuery}
                  />
                </View>
              )}
              <ScrollView style={cf.optionList} nestedScrollEnabled>
                {filteredOptions.length === 0 ? (
                  <Text style={cf.emptyText}>No matches</Text>
                ) : (
                  filteredOptions.map((opt) => {
                    const checked = selected.includes(opt.value);
                    return (
                      <Pressable key={opt.value} style={cf.option} onPress={() => toggleValue(opt.value)}>
                        <View style={[cf.checkbox, checked && cf.checkboxChecked]}>
                          {checked && <MaterialIcons name="check" size={10} color="#fff" />}
                        </View>
                        <Text style={cf.optionText} numberOfLines={1}>{opt.label}</Text>
                      </Pressable>
                    );
                  })
                )}
              </ScrollView>
              {active && (
                <Pressable style={cf.clearBtn} onPress={() => onChange([])}>
                  <Text style={cf.clearBtnText}>Clear filter</Text>
                </Pressable>
              )}
            </View>
          )}
        </View>
      </Modal>
    </View>
  );
};

export default RosterColumnFilter;

const cf = StyleSheet.create({
  wrap:        { flexDirection: "row", alignItems: "center" },
  trigger:     { flexDirection: "row", alignItems: "center", gap: 4 },
  triggerText: { fontSize: 11, color: "#6b7280", letterSpacing: 0.4, fontWeight: "700" },
  iconWrap:    { flexDirection: "row", alignItems: "center", gap: 2, padding: 2, borderRadius: 4 },
  iconWrapActive: { backgroundColor: "#EEF5FF" },
  badge:       { backgroundColor: "#034895", borderRadius: 8, minWidth: 14, height: 14, alignItems: "center", justifyContent: "center", paddingHorizontal: 3 },
  badgeText:   { fontSize: 9, color: "#fff", fontWeight: "700" },

  panel: {
    position: "absolute",
    width: PANEL_WIDTH,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#E4E9F2",
    borderRadius: 8,
    paddingVertical: 6,
    ...({ boxShadow: "0 8px 24px rgba(0,20,60,0.14)" } as any),
  } as any,

  searchWrap:   { flexDirection: "row", alignItems: "center", gap: 6, marginHorizontal: 8, marginBottom: 6, paddingHorizontal: 8, paddingVertical: 6, backgroundColor: "#F7F9FC", borderRadius: 6, borderWidth: 1, borderColor: "#E4E9F2" },
  searchInput:  { flex: 1, fontSize: 12, color: "#2d2d2d", padding: 0, outlineWidth: 0, outline: "none" } as any,

  optionList:  { maxHeight: 200 },
  emptyText:   { fontSize: 12, color: "#9a9a9a", paddingHorizontal: 12, paddingVertical: 8 },
  option:      { flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 10, paddingVertical: 7 },
  optionText:  { fontSize: 12.5, color: "#2d2d2d", flex: 1 },
  checkbox:    { width: 15, height: 15, borderRadius: 3, borderWidth: 1.5, borderColor: "#D1D5DB", alignItems: "center", justifyContent: "center", backgroundColor: "#fff" },
  checkboxChecked: { backgroundColor: "#034895", borderColor: "#034895" },

  clearBtn:     { borderTopWidth: 1, borderTopColor: "#F0F0F0", marginTop: 4, paddingTop: 6, paddingHorizontal: 10 },
  clearBtnText: { fontSize: 11.5, color: "#034895", fontWeight: "600" },
});
